/**
 * WelcomeTour - Tour guidé avec spotlight sur les éléments clés
 * Design moderne avec animations fluides
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { createPortal } from 'react-dom';
import { Button } from './ui/button';
import confetti from 'canvas-confetti';

import { TOUR_CONFIGS } from './tourConfigs';
import { SpotlightOverlay, TourTooltip, WelcomeModal, CompletionModal } from './WelcomeTourParts';

const isTestEnvironment = () => {
  // Check URL parameters
  const urlParams = new URLSearchParams(window.location.search);
  if (urlParams.get('tour') === 'false' || urlParams.get('notour') === 'true') {
    return true;
  }
  
  // Check if preview environment (Emergent preview URLs)
  const hostname = window.location.hostname;
  if (hostname.includes('preview.emergentagent.com') || hostname.includes('localhost')) {
    return true;
  }
  
  // Check environment variable
  if (process.env.REACT_APP_DISABLE_TOUR === 'true') {
    return true;
  }
  
  return false;
};

// Main WelcomeTour Component
export default function WelcomeTour({ space = 'member', forceShow = false }) {
  const [phase, setPhase] = useState('idle'); // idle, welcome, touring, complete
  const [currentStep, setCurrentStep] = useState(0);
  const [targetRect, setTargetRect] = useState(null);
  const location = useLocation();
  
  const tourConfig = TOUR_CONFIGS[space] || TOUR_CONFIGS.member;
  const currentTourStep = tourConfig.steps[currentStep];

  // Check if tour should show
  useEffect(() => {
    // Disable tour in test/preview environments unless explicitly forced
    if (isTestEnvironment() && !forceShow) {
      return;
    }

    // V36.2 — Ne pas démarrer le tour tant que l'utilisateur doit définir
    // son mot de passe (modale SetPasswordModal active via ?must_set_password=1).
    // Une fois la modale fermée, `location.search` change → cet effet se
    // ré-exécute et le tour démarre normalement.
    if (new URLSearchParams(location.search).get('must_set_password') === '1') {
      return;
    }

    const tourKey = `welcome_tour_${space}_completed`;
    const sessionSkipKey = `welcome_tour_${space}_skipped_session`;
    const hasCompleted = localStorage.getItem(tourKey);
    const hasSkippedThisSession = sessionStorage.getItem(sessionSkipKey);
    
    // Don't show if already completed OR skipped in this session
    if (forceShow || (!hasCompleted && !hasSkippedThisSession)) {
      // Delay to let the page render
      const timer = setTimeout(() => {
        setPhase('welcome');
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [space, forceShow, location.search]);

  // Find and highlight target element (avec fallback flottant si absent)
  useEffect(() => {
    if (phase !== 'touring' || !currentTourStep) return;

    const findTarget = () => {
      const target = document.querySelector(currentTourStep.target);
      if (target) {
        const rect = target.getBoundingClientRect();
        setTargetRect({ ...rect.toJSON?.(), ...rect, _isFloating: false });
        target.scrollIntoView({ behavior: 'smooth', block: 'center' });
      } else {
        // Target introuvable → tooltip flottante au centre, sans spotlight.
        const w = window.innerWidth;
        const h = window.innerHeight;
        setTargetRect({
          top: h / 2,
          left: w / 2,
          width: 0,
          height: 0,
          right: w / 2,
          bottom: h / 2,
          x: w / 2,
          y: h / 2,
          _isFloating: true,
        });
        // eslint-disable-next-line no-console
        console.warn(`[WelcomeTour] Target introuvable: ${currentTourStep.target} — affichage flottant`);
      }
    };

    const timer = setTimeout(findTarget, 300);
    return () => clearTimeout(timer);
  }, [phase, currentStep, currentTourStep]);

  // Handle window resize
  useEffect(() => {
    if (phase !== 'touring') return;

    const handleResize = () => {
      if (currentTourStep) {
        const target = document.querySelector(currentTourStep.target);
        if (target) {
          setTargetRect(target.getBoundingClientRect());
        }
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [phase, currentTourStep]);

  const handleStart = () => {
    setCurrentStep(0);
    setPhase('touring');
  };

  const handleNext = () => {
    if (currentStep < tourConfig.steps.length - 1) {
      setCurrentStep(prev => prev + 1);
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const handleSkip = useCallback(() => {
    const tourKey = `welcome_tour_${space}_completed`;
    const sessionSkipKey = `welcome_tour_${space}_skipped_session`;
    // Mark as skipped for this session (won't show again until page refresh or new session)
    sessionStorage.setItem(sessionSkipKey, 'true');
    // Also mark as completed in localStorage to prevent future shows
    localStorage.setItem(tourKey, 'true');
    setPhase('idle');
    setTargetRect(null);
  }, [space]);

  const handleFinish = useCallback(() => {
    const tourKey = `welcome_tour_${space}_completed`;
    localStorage.setItem(tourKey, 'true');
    setPhase('complete');
  }, [space]);

  const handleComplete = useCallback(() => {
    setPhase('idle');
    setTargetRect(null);
  }, []);

  // Reset tour function (for testing/help center)
  // V35.44 — Nettoyage exhaustif des flags (localStorage + sessionStorage) + démarrage direct
  const resetTour = useCallback(() => {
    const tourKey = `welcome_tour_${space}_completed`;
    const sessionSkipKey = `welcome_tour_${space}_skipped_session`;
    try {
      localStorage.removeItem(tourKey);
      sessionStorage.removeItem(sessionSkipKey);
    } catch { /* storage indisponible : on continue */ }
    setCurrentStep(0);
    setTargetRect(null);
    // Force le re-render même si on est déjà sur welcome (cas où l'utilisateur clique 2x)
    setPhase('idle');
    setTimeout(() => setPhase('welcome'), 50);
  }, [space]);

  // Expose reset function globally for help center access
  useEffect(() => {
    window.resetWelcomeTour = resetTour;
    return () => {
      delete window.resetWelcomeTour;
    };
  }, [resetTour]);

  if (phase === 'idle') return null;

  return (
    <>
      {phase === 'welcome' && (
        <WelcomeModal
          tourConfig={tourConfig}
          onStart={handleStart}
          onSkip={handleSkip}
        />
      )}

      {phase === 'touring' && (
        <>
          <SpotlightOverlay
            targetRect={targetRect}
            isVisible={!!targetRect}
          />
          {targetRect && currentTourStep && (
            <TourTooltip
              step={currentTourStep}
              currentStep={currentStep}
              totalSteps={tourConfig.steps.length}
              position={currentTourStep.position}
              targetRect={targetRect}
              tourConfig={tourConfig}
              onNext={handleNext}
              onPrev={handlePrev}
              onSkip={handleSkip}
              onFinish={handleFinish}
            />
          )}
        </>
      )}

      {phase === 'complete' && (
        <CompletionModal
          tourConfig={tourConfig}
          onClose={handleComplete}
        />
      )}
    </>
  );
}

// Export config for external use
export { TOUR_CONFIGS };

